import React, { useState, useEffect } from 'react';
import { Loader2, Save, DollarSign, Percent } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';

const fmt = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(v) || 0);

function Field({ label, hint, value, onChange }) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground mb-1 block">{label}</label>
      <Input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 text-sm text-right"
        data-role="mono"
      />
      {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

function Subtotal({ label, value }) {
  return (
    <div className="flex justify-between items-center text-xs bg-muted/50 rounded px-2.5 py-1.5 mt-3">
      <span className="font-medium text-muted-foreground">{label}</span>
      <span className="font-semibold" data-role="mono">{fmt(value)}</span>
    </div>
  );
}

export default function RevenueLedgerModal({ open, onOpenChange, project, onSave }) {
  const [rb, setRb] = useState({});
  const [sc, setSc] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && project) {
      const raw = project.revenue_breakdown || {};
      setRb({
        one_time_charge: raw.one_time_charge ?? project.project_billed ?? 0,
        monthly_recurring: raw.monthly_recurring ?? 0,
        annual_recurring: raw.annual_recurring ?? 0,
      });
      const rawSc = project.sales_commission || {};
      setSc({
        one_time_type: rawSc.one_time_type ?? 'fixed',
        one_time_value: rawSc.one_time_value ?? 0,
        recurring_type: rawSc.recurring_type ?? 'percentage',
        recurring_value: rawSc.recurring_value ?? rawSc.recurring_pct ?? 0,
        subtract_from_net: rawSc.subtract_from_net ?? false,
      });
    }
  }, [open, project]);

  const oneTime = parseFloat(rb.one_time_charge) || 0;
  const monthlyAnnualized = (parseFloat(rb.monthly_recurring) || 0) * 12;
  const annual = parseFloat(rb.annual_recurring) || 0;
  const recurringTotal = monthlyAnnualized + annual;
  const grandTotal = oneTime + recurringTotal;

  // Commission calculations
  const commOneTime = sc.one_time_type === 'percentage'
    ? oneTime * ((parseFloat(sc.one_time_value) || 0) / 100)
    : (parseFloat(sc.one_time_value) || 0);
  const monthlyRev = parseFloat(rb.monthly_recurring) || 0;
  const annualRev = parseFloat(rb.annual_recurring) || 0;
  const recurringRevAnnualized = (monthlyRev * 12) + annualRev;
  const recurringVal = parseFloat(sc.recurring_value) || 0;
  const commRecurring = sc.recurring_type === 'fixed'
    ? recurringVal * 12
    : recurringRevAnnualized * (recurringVal / 100);
  const commTotal = commOneTime + commRecurring;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(project.id, {
        one_time_charge: parseFloat(rb.one_time_charge) || 0,
        monthly_recurring: parseFloat(rb.monthly_recurring) || 0,
        annual_recurring: parseFloat(rb.annual_recurring) || 0,
      }, {
        one_time_type: sc.one_time_type,
        one_time_value: parseFloat(sc.one_time_value) || 0,
        one_time_amount: Math.round(commOneTime * 100) / 100,
        recurring_type: sc.recurring_type,
        recurring_value: recurringVal,
        recurring_pct: sc.recurring_type === 'percentage' ? recurringVal : 0,
        recurring_amount: Math.round(commRecurring * 100) / 100,
        subtract_from_net: sc.subtract_from_net,
      });
      onOpenChange(false);
    } catch {} finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 flex flex-col" style={{ maxHeight: '80vh' }}>
        <DialogHeader className="px-6 pt-5 pb-3 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2" data-role="heading">
            <DollarSign className="h-5 w-5 text-emerald-600" />
            Project Revenue Ledger
          </DialogTitle>
          <p className="text-xs text-muted-foreground">{project?.project_name}</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-4">
          {/* One-Time Revenue */}
          <div className="border rounded-lg p-4 space-y-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-foreground">One-Time Revenue</h3>
            <Field
              label="One-Time Charge"
              value={rb.one_time_charge}
              onChange={(v) => setRb(prev => ({ ...prev, one_time_charge: v }))}
              hint="Setup fee, design fee, project charge"
            />
            <Subtotal label="One-Time Subtotal" value={oneTime} />
            {sc.subtract_from_net && commOneTime > 0 && (
              <div className="flex justify-between items-center text-xs px-2.5 py-1">
                <span className="text-amber-600 font-medium">Net (after commission)</span>
                <span className="font-semibold text-amber-700" data-role="mono">{fmt(oneTime - commOneTime)}</span>
              </div>
            )}
          </div>

          {/* Recurring Revenue */}
          <div className="border rounded-lg p-4 space-y-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-foreground">Annualized Recurring</h3>
            <Field
              label="Monthly Recurring Charge"
              value={rb.monthly_recurring}
              onChange={(v) => setRb(prev => ({ ...prev, monthly_recurring: v }))}
              hint={`${fmt(rb.monthly_recurring)}/mo x 12 = ${fmt(monthlyAnnualized)} annualized`}
            />
            <Field
              label="Annual Recurring Charge"
              value={rb.annual_recurring}
              onChange={(v) => setRb(prev => ({ ...prev, annual_recurring: v }))}
              hint={`${fmt(rb.annual_recurring)}/yr \u00f7 12 = ${fmt(annual / 12)}/mo`}
            />
            <Subtotal label="Recurring Subtotal" value={recurringTotal} />
            {sc.subtract_from_net && commRecurring > 0 && (
              <div className="flex justify-between items-center text-xs px-2.5 py-1">
                <span className="text-amber-600 font-medium">Net (after commission)</span>
                <span className="font-semibold text-amber-700" data-role="mono">{fmt(recurringTotal - commRecurring)}</span>
              </div>
            )}
          </div>

          {/* Sales Commission */}
          <div className="border rounded-lg p-4 space-y-3 border-amber-200/60 bg-amber-50/20">
            <div className="flex items-center justify-between">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-foreground">Sales Commission</h3>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground">Subtract from net</span>
                <Switch
                  checked={sc.subtract_from_net}
                  onCheckedChange={(v) => setSc(prev => ({ ...prev, subtract_from_net: v }))}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">One-Time Commission</label>
              <div className="flex gap-2">
                <select
                  value={sc.one_time_type}
                  onChange={(e) => setSc(prev => ({ ...prev, one_time_type: e.target.value }))}
                  className="text-xs border rounded px-2 py-1 bg-background w-[80px] h-8"
                >
                  <option value="fixed">$</option>
                  <option value="percentage">%</option>
                </select>
                <Input
                  type="text" inputMode="decimal"
                  value={sc.one_time_value}
                  onChange={(e) => setSc(prev => ({ ...prev, one_time_value: e.target.value }))}
                  className="h-8 text-sm text-right flex-1" data-role="mono"
                  placeholder="0"
                />
              </div>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {sc.one_time_type === 'percentage'
                  ? `${sc.one_time_value || 0}% of ${fmt(oneTime)} = ${fmt(commOneTime)}`
                  : `Fixed: ${fmt(sc.one_time_value)}`}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Recurring Commission</label>
              <div className="flex gap-2">
                <select
                  value={sc.recurring_type}
                  onChange={(e) => setSc(prev => ({ ...prev, recurring_type: e.target.value }))}
                  className="text-xs border rounded px-2 py-1 bg-background w-[80px] h-8"
                >
                  <option value="fixed">$/mo</option>
                  <option value="percentage">%</option>
                </select>
                <Input
                  type="text" inputMode="decimal"
                  value={sc.recurring_value}
                  onChange={(e) => setSc(prev => ({ ...prev, recurring_value: e.target.value }))}
                  className="h-8 text-sm text-right flex-1" data-role="mono"
                  placeholder="0"
                />
              </div>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {sc.recurring_type === 'fixed'
                  ? `${fmt(sc.recurring_value)}/mo = ${fmt((parseFloat(sc.recurring_value) || 0) * 12)}/yr`
                  : `${sc.recurring_value || 0}% of ${fmt(recurringRevAnnualized)} = ${fmt(commRecurring)}/yr`}
              </p>
            </div>
            <Subtotal label="Total Commission (Annual)" value={commTotal} />
          </div>
        </div>

        {/* Sticky footer */}
        <div className="flex-shrink-0 border-t bg-card px-6 py-3 space-y-3">
          {(() => {
            const isNet = sc.subtract_from_net && commTotal > 0;
            const displayTotal = isNet ? grandTotal - commTotal : grandTotal;
            const bgClass = isNet ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200';
            const titleColor = isNet ? 'text-amber-900' : 'text-emerald-900';
            const subtitleColor = isNet ? 'text-amber-600' : 'text-emerald-600';
            const valueColor = isNet ? 'text-amber-700' : 'text-emerald-700';
            return (
              <div className={`flex justify-between items-center ${bgClass} border rounded-lg px-4 py-2.5`}>
                <div>
                  <span className={`text-sm font-semibold block ${titleColor}`}>
                    {isNet ? 'Net Annualized Revenue' : 'Total Annualized Revenue'}
                  </span>
                  <span className={`text-[10px] ${subtitleColor}`}>
                    {isNet ? 'First-year net revenue (after commission)' : 'First-year revenue'}
                  </span>
                </div>
                <span className={`text-lg font-bold ${valueColor}`} data-role="mono">{fmt(displayTotal)}</span>
              </div>
            );
          })()}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Save Revenue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
